import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiFetch } from '../utils/api';

// Divide una lista de destinatarios por comas, IGNORANDO las comas que están
// dentro de un nombre entre comillas ("Apellido, Nombre") o dentro de <...>.
// Evita que `"Apellido, Nombre" <a@b.com>` se parta en dos chips inválidos.
function splitRecipients(str) {
  const out = [];
  let cur = '';
  let inAngle = false;
  let inQuote = false;
  for (const ch of String(str || '')) {
    if (ch === '"') { inQuote = !inQuote; cur += ch; continue; }
    if (ch === '<' && !inQuote) { inAngle = true; cur += ch; continue; }
    if (ch === '>' && !inQuote) { inAngle = false; cur += ch; continue; }
    if ((ch === ',' || ch === ';') && !inAngle && !inQuote) {
      const t = cur.trim();
      if (t) out.push(t);
      cur = '';
      continue;
    }
    cur += ch;
  }
  const last = cur.trim();
  if (last) out.push(last);
  return out;
}

export default function EmailRecipientInput({ value, onChange, placeholder }) {
  const [inputText, setInputText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const chips = useMemo(() => splitRecipients(value), [value]);

  const updateValue = useCallback((newChips) => {
    onChange(newChips.join(', '));
  }, [onChange]);

  const addChip = useCallback((email) => {
    const trimmed = email.trim();
    if (!trimmed || chips.includes(trimmed)) return;
    updateValue([...chips, trimmed]);
    setInputText('');
    setSuggestions([]);
    setShowDropdown(false);
    setHighlightedIndex(-1);
  }, [chips, updateValue]);

  const removeChip = useCallback((index) => {
    const next = chips.filter((_, i) => i !== index);
    updateValue(next);
  }, [chips, updateValue]);

  // Calculate dropdown position relative to viewport (fixed positioning to escape overflow clipping)
  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom,
      left: rect.left,
      width: rect.width
    });
  }, []);

  // Debounced search
  useEffect(() => {
    if (inputText.length < 1) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await apiFetch(`/api/email/autocomplete?q=${encodeURIComponent(inputText)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const filtered = (Array.isArray(data) ? data : []).filter(s => !chips.includes(s.email));
          setSuggestions(filtered);
          setShowDropdown(filtered.length > 0);
          setHighlightedIndex(-1);
          updateDropdownPosition();
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [inputText, chips, updateDropdownPosition]);

  // Click outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Update position on scroll/resize while dropdown is open
  useEffect(() => {
    if (!showDropdown) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showDropdown, updateDropdownPosition]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && showDropdown) {
      e.preventDefault();
      setHighlightedIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp' && showDropdown) {
      e.preventDefault();
      setHighlightedIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        addChip(suggestions[highlightedIndex].email);
      } else if (inputText.includes('@')) {
        addChip(inputText);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    } else if (e.key === 'Backspace' && inputText === '' && chips.length > 0) {
      removeChip(chips.length - 1);
    } else if ((e.key === ',' || e.key === 'Tab') && inputText.trim()) {
      if (inputText.includes('@')) {
        e.preventDefault();
        addChip(inputText);
      }
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div
        className="form-control d-flex flex-wrap align-items-center gap-1"
        style={{ minHeight: 38, height: 'auto', cursor: 'text', paddingTop: 4, paddingBottom: 4 }}
        onClick={() => inputRef.current?.focus()}
      >
        {chips.map((chip, i) => (
          <span key={i} className="badge bg-primary d-inline-flex align-items-center gap-1" style={{ fontSize: '0.85em' }}>
            {chip}
            <button
              type="button"
              className="btn-close btn-close-white"
              style={{ fontSize: '0.55em', marginLeft: 2 }}
              onClick={(e) => { e.stopPropagation(); removeChip(i); }}
            />
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Confirmar lo escrito al salir del campo: si el usuario teclea la dirección
            // y pulsa directamente "Enviar" (sin Enter/coma), el texto quedaba en el
            // buffer interno y el formulario veía el destinatario vacío.
            const pending = inputText.trim();
            if (pending && pending.includes('@')) addChip(pending);
          }}
          onFocus={() => { if (suggestions.length > 0) { updateDropdownPosition(); setShowDropdown(true); } }}
          placeholder={chips.length === 0 ? (placeholder || '') : ''}
          style={{ border: 'none', outline: 'none', flex: '1 1 80px', minWidth: 80, background: 'transparent' }}
          autoComplete="off"
        />
        {loading && <span className="spinner-border spinner-border-sm text-muted" style={{ width: 16, height: 16 }} />}
      </div>
      {showDropdown && (
        <div
          className="list-group shadow"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
            maxHeight: 260,
            overflowY: 'auto',
            borderRadius: '0 0 6px 6px',
            border: '1px solid #dee2e6',
            borderTop: 'none'
          }}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.email}
              type="button"
              className={`list-group-item list-group-item-action py-2 px-3${i === highlightedIndex ? ' active' : ''}`}
              onMouseEnter={() => setHighlightedIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); addChip(s.email); }}
            >
              <div style={{ fontSize: '0.9em' }}>
                {s.name
                  ? <><strong>{s.name}</strong> <span className="text-muted">— {s.email}</span></>
                  : s.email
                }
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
